import api from "../../services/api";
import { toast } from "react-toastify";
import { formatDate, stringToDate } from "../utils/useFormat";

export async function listTrainees(props) {
  const { query, config } = props;
  try {
    let { data: _trainees } = await api.get(`trainees${query}`, config);
    _trainees.data = _trainees.data?.map(__trainee => formatTrainee(__trainee));
    return _trainees;
  } catch (__error) {
    console.log("__error", __error);
    toast.error("Problemas ao tentar carregar dados dos Formandos.");
  }
}

export async function getTrainee(props) {
  const { id, query, config } = props;
  try {
    let { data: _trainee } = await api.get(`trainees/${id}${query}`, config);
    _trainee = formatTrainee(_trainee, true);
    return _trainee;
  } catch (__error) {
    console.log("__error", __error);
    toast.error("Problemas ao tentar carregar dados do Formando.");
  }
}

/**
 * Vínculo: o status único do Formando, numa coluna só.
 *
 * Antes eram duas colunas — "Acesso" e "Vínculo" — e os gestores tinham que
 * cruzar as duas na cabeça, porque "Vínculo: Encerrado" sozinho não distinguia
 * quem saiu devendo multa de quem já quitou; quem dizia isso era a coluna
 * "Acesso", ou seja, informação financeira exibida com nome de permissão.
 *
 * Agora é um eixo linear que responde "o que falta fazer com esse formando?", e
 * a cor agrupa em três leituras: verde (ok), amarelo (tem pendência) e cinza
 * (acabou). O valor vem pronto do backend (`trainees.bond_status`), derivado
 * pelo BondStatusService — aqui só se traduz para o rótulo da tela.
 */
export const BOND_LABELS = {
  aguardando_aprovacao: "Aguardando Aprovação",
  aguardando_aceite: "Aguardando Aceite",
  ativo: "Ativo",
  encerrado_aguardando_distrato: "Encerrado - Aguardando Distrato",
  encerrado_multa_aberto: "Encerrado - Multa em Aberto",
  encerrado_reembolso_pendente: "Encerrado - Reembolso Pendente",
  encerrado: "Encerrado",
};

export const BOND_TONES = {
  aguardando_aprovacao: "warning",
  aguardando_aceite: "warning-other",
  ativo: "success",
  encerrado_aguardando_distrato: "warning",
  encerrado_multa_aberto: "warning",
  encerrado_reembolso_pendente: "warning",
  encerrado: "neutral",
};

/**
 * Versão curta para a coluna da tabela, onde o prefixo "Encerrado - " se repete
 * linha após linha e a opacidade reduzida da linha já diz que o vínculo acabou.
 * Na ficha do formando o rótulo completo é mantido.
 */
export const BOND_SHORT_LABELS = {
  encerrado_aguardando_distrato: "Aguardando Distrato",
  encerrado_multa_aberto: "Multa em Aberto",
  encerrado_reembolso_pendente: "Reembolso Pendente",
};

export const BOND_FILTER_OPTIONS = [
  { value: null, label: "-- Todos --" },
  ...Object.keys(BOND_LABELS).map(value => ({ value, label: BOND_LABELS[value] })),
];

/**
 * Aceite: se o formando fez o aceite virtual do contrato (`trainees.aceite_virtual`,
 * tinyint nullable) e quando (`trainees.date_aceite`). NULL conta como "Não", igual
 * ao BondStatusService (`COALESCE(aceite_virtual, 0) = 0`); qualquer outro valor
 * aparece como "?" em vez de ser forçado para Sim/Não.
 */
export function formatAceite(__trainee) {
  const _value = __trainee?.aceite_virtual;
  if ([1, true, "1"].includes(_value)) {
    const _date = !!__trainee?.date_aceite ? stringToDate(__trainee.date_aceite) : null;
    return {
      aceite: "Sim",
      aceite_tone: "success",
      aceite_tip:
        !!_date && !isNaN(_date.getTime()) ? `Aceite em ${formatDate(_date, 5)}` : "Data do aceite não registrada",
    };
  }
  if ([0, false, "0", null, undefined].includes(_value)) {
    return { aceite: "Não", aceite_tone: "warning-other", aceite_tip: "Aceite não realizado" };
  }
  return { aceite: "?", aceite_tone: "neutral", aceite_tip: "Status de aceite não reconhecido" };
}

export function formatTrainee(__trainee, is_single) {
  let _commercial_label = "";
  if (is_single) {
    const _commercial = __trainee?.commercials;
    const _group_name = !!_commercial?.contract_number_legacy ? ` - ${_commercial?.contract_number_legacy}` : "";
    _commercial_label = `${_commercial?.course?.title} ${_commercial?.institution?.title} ${_commercial?.year}.${_commercial?.period}${_group_name}`;
  } else {
    const _group_name = !!__trainee?.commercials?.contract_number_legacy
      ? ` - ${__trainee?.commercials?.contract_number_legacy}`
      : "";
    _commercial_label = `${__trainee?.courses?.[0]?.title} ${__trainee?.institution?.title} ${__trainee?.commercials?.year}.${__trainee?.commercials?.period}${_group_name}`;
  }
  return {
    ...__trainee,
    access:
      !__trainee?.active || __trainee?.access_status === "encerrado"
        ? "Revogado"
        : __trainee?.access_status === "limitado"
        ? "Acesso Limitado"
        : !__trainee?.approve_by_team
        ? "Aguardando Aprovação"
        : !__trainee?.aceite_virtual
        ? "Aguardando Aceite"
        : !!__trainee?.aceite_virtual
        ? "Autorizado"
        : "?",
    // fallback para o caso de um formando anterior à migration do bond_status
    // (ou de um endpoint que não devolva a coluna): pelo menos não mente
    bond: BOND_LABELS[__trainee?.bond_status] || (__trainee?.cancel ? "Encerrado" : "Ativo"),
    bond_tone: BOND_TONES[__trainee?.bond_status] || (__trainee?.cancel ? "neutral" : "success"),
    bond_short: BOND_SHORT_LABELS[__trainee?.bond_status] || null,
    ...formatAceite(__trainee),
    // o acesso do formando é assunto exclusivo do Administrador do Sistema; para
    // os demais gestores só o estado excepcional aparece (ver TraineeSubMenu)
    access_revoked: !__trainee?.active,
    commercial_label: _commercial_label,
  };
}
